"use client";

import { Currency, PackageType, REVENUECAT_CONFIG } from "@/lib/revenuecat/config";
import { getPriceForCurrency } from "@/lib/revenuecat/currency-detector";
import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { PlanValueBanner } from "./PlanValueBanner";

interface PricingCardProps {
  packageType: PackageType;
  currency: Currency;
  onSelectAction: (packageType: PackageType) => void;
  isLoading?: boolean;
  disabled?: boolean;
  isSelected?: boolean;
  showFreeTrial?: boolean;
  overridePriceFormatted?: string;
  discountLabel?: string;
  originalPriceFormatted?: string;
  promotionalPeriod?: string;
  variant?: "default" | "zen" | "landing";
  periodTextOverride?: string;
  billingDetail?: string;
}

export function PricingCard({
  packageType,
  currency,
  onSelectAction,
  isLoading = false,
  disabled = false,
  isSelected = false,
  showFreeTrial = false,
  overridePriceFormatted,
  discountLabel,
  originalPriceFormatted,
  promotionalPeriod,
  variant = "default",
  periodTextOverride,
  billingDetail,
}: PricingCardProps) {
  const pkg = REVENUECAT_CONFIG.packages[packageType];
  const shouldReduceMotion = useReducedMotion();
  const isMostPopular = pkg.mostPopular;
  const pricing = getPriceForCurrency(packageType, currency);
  const displayedPrice = overridePriceFormatted ?? pricing.formatted;

  // Determine period text
  const periodText =
    packageType === "weekly" ? "per week" : packageType === "monthly" ? "per month" : "per year";

  return (
    <motion.div
      initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={disabled || shouldReduceMotion ? undefined : { y: -3 }}
      className={`relative flex h-full flex-col overflow-hidden transition-all duration-300 ${
        variant === "landing" ? "rounded-2xl" : "rounded-xl md:rounded-2xl"
      } ${
        variant === "landing"
          ? "border border-white/15 bg-[#15161B] hover:border-white/30"
          : variant === "zen"
            ? isMostPopular
              ? "bg-stone-800/80 border-2 border-stone-400/50 shadow-xl shadow-stone-900/50"
              : "bg-stone-900/50 border border-stone-800 hover:border-stone-600 hover:bg-stone-800/50"
            : isMostPopular
              ? "border border-[#FD954F]/50 bg-[#15161B]"
              : "border border-white/15 bg-[#15161B] hover:border-white/30"
      } ${
        isSelected
          ? variant === "zen"
            ? "ring-1 ring-stone-400 ring-offset-1 ring-offset-stone-900"
            : "ring-2 ring-[#FD954F] ring-offset-2 ring-offset-[#0F0F14]"
          : ""
      } ${disabled || isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      {pkg.badge && (
        <PlanValueBanner packageType={packageType} variant={variant === "zen" ? "zen" : "standard"}>
          {pkg.badge}
        </PlanValueBanner>
      )}

      <div className={`flex flex-1 flex-col ${variant === "landing" ? "p-6" : "p-4 md:p-6"}`}>
        {/* Plan Name */}
        <h3
          className={`mb-2 text-lg font-bold text-white md:text-2xl ${
            variant === "landing" ? "text-left" : "text-center"
          }`}
        >
          {pkg.displayName}
        </h3>

        {/* Description */}
        {pkg.description && (
          <p
            className={`mb-3 text-xs text-gray-400 md:text-sm ${
              variant === "landing" ? "text-left" : "text-center"
            }`}
          >
            {pkg.description}
          </p>
        )}

        {/* Price */}
        <div className={`mb-3 ${variant === "landing" ? "text-left" : "text-center"}`}>
          <div
            className={`flex flex-wrap items-baseline gap-2 ${
              variant === "landing" ? "justify-start" : "justify-center"
            }`}
          >
            {originalPriceFormatted && (
              <span className="text-lg md:text-2xl font-semibold text-gray-500 line-through">
                {originalPriceFormatted}
              </span>
            )}
            <span className="text-2xl md:text-4xl font-bold text-white">{displayedPrice}</span>
          </div>
          <span className="mt-0.5 block text-xs text-gray-400 md:text-sm">
            {periodTextOverride ?? periodText}
          </span>
          {billingDetail && (
            <span className="mt-1 block text-xs font-medium text-gray-300 md:text-sm">
              {billingDetail}
            </span>
          )}
        </div>

        {/* Discount Label */}
        {discountLabel && (
          <div className="mb-4">
            <div className="bg-green-500/20 border border-green-500/50 rounded-lg px-3 py-1.5 text-center inline-block w-full">
              <span className="text-green-400 font-semibold text-xs md:text-sm">
                {discountLabel}
              </span>
              {promotionalPeriod && (
                <div className="text-green-300 text-xs mt-1">
                  Promotional rate for {promotionalPeriod}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Free Trial Tag */}
        {showFreeTrial && (
          <div className="mb-4">
            <div className="bg-green-500/20 border border-green-500/50 rounded-lg px-3 py-1.5 text-center inline-block w-full">
              <span className="text-green-400 font-semibold text-xs md:text-sm">
                Free for 3 days
              </span>
            </div>
          </div>
        )}

        {/* Features */}
        <ul className="flex flex-1 flex-col items-start space-y-1.5 md:space-y-3">
          {pkg.features.map((feature, index) => (
            <li
              key={index}
              className="flex items-start gap-1.5 md:gap-2 text-xs md:text-sm text-gray-300"
            >
              <Check
                className={`w-3.5 h-3.5 md:w-5 md:h-5 flex-shrink-0 mt-0.5 ${
                  variant === "zen" ? "text-stone-400" : "text-orange-500"
                }`}
              />
              <span className="leading-snug">{feature}</span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => onSelectAction(packageType)}
          disabled={disabled || isLoading}
          aria-pressed={isSelected}
          className={`mt-6 inline-flex min-h-12 w-full items-center justify-center whitespace-nowrap rounded-xl px-4 py-3 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
            variant === "landing"
              ? "bg-[#FD954F] text-[#1C1C1E] hover:bg-[#F28747] focus-visible:ring-white focus-visible:ring-offset-[#15161B]"
              : variant === "zen"
                ? "bg-stone-100 text-stone-950 hover:bg-white focus-visible:ring-stone-300 focus-visible:ring-offset-stone-900"
                : "bg-[#FD954F] text-[#1C1C1E] hover:bg-[#F28747] focus-visible:ring-[#FD954F] focus-visible:ring-offset-[#0F0F14]"
          }`}
        >
          {isLoading
            ? "Opening checkout..."
            : showFreeTrial
              ? "Start 3-day free trial"
              : `Choose ${pkg.displayName}`}
        </button>
      </div>
    </motion.div>
  );
}
